<template lang="pug">
.action-button-wrap
  //- 按钮区（数据就绪后再渲染，避免曝光早于接口返回导致 clk_name 取到兜底值）
  .action-button(
    v-if="taskGroup.subsidyDetailInfos"
    :class="buttonClass"
    :style="buttonBg"
    v-platform-expose="reportButtonExpose"
    @click="handleClick"
  )
    .btn-badge(v-if="canClick") 最高领{{ currentBountyCount }}奖励金
    img.btn-play-icon(v-if="canClick" src="https://m.hellobike.com/9465873577.png")
    span.btn-text {{ buttonText }}

  //- 倒计时（不可点击时显示距下个任务的剩余时间）
  .countdown-wrap(v-if="!canClick && countdownSeconds > 0")
    span 再过
    CountDown.count-down-num(
      :durationMs="countdownSeconds"
      format="HH:mm:ss"
      expiredText=''
      @finish="handleCountdownFinish"
    )
    span 领{{ tasksByTime.next && tasksByTime.next.taskName || '补贴' }}

  //- 引导弹窗（当前任务时段首次进入）
  .popup(
    v-if="showGuidePopup"
    @click.self="showGuidePopup = false"
  )
    .popup-box
      .popup-title 恭喜获得{{ tasksByTime.current && tasksByTime.current.taskName || '补贴' }}
      .popup-amount-wrap
        span.popup-amount {{ currentBountyCount }}
        span.popup-amount-text 奖励金
      .popup-btn(@click="handleGuideAction") 看视频领取
    img.popup-close(src="https://m.hellobike.com/04a769a70d.png" @click="showGuidePopup = false")

  //- 领奖弹窗（看完视频成功后）
  .popup(
    v-if="showRewardPopup"
    @click.self="closeRewardPopup"
  )
    .popup-box
      .popup-title 恭喜您累积获得
      .popup-amount-wrap
        span.popup-amount {{ rewardAmount }}
        span.popup-amount-text 奖励金
      .popup-btn(@click="closeRewardPopup") 开心收下
    img.popup-close(src="https://m.hellobike.com/04a769a70d.png" @click="closeRewardPopup")

  //- 小组件引导弹窗（首次进入且不在时段/任务已完成）
  .popup.add-components(
    v-if="showWidgetPopup"
    @click.self="showWidgetPopup = false"
  )
    .popup-box
      .popup-title {{ tasksByTime.next && tasksByTime.next.taskName || '补贴' }}即将开始
      .popup-amount-wrap
        span.popup-amount {{ tasksByTime.next && tasksByTime.next.taskBountyCount || 0 }}
        span.popup-amount-text 奖励金
      .popup-btn(@click="handleWidgetAction") 添加小组件不错过
    img.popup-close(src="https://m.hellobike.com/04a769a70d.png" @click="showWidgetPopup = false")</template>

<script>
import Toast from '@/components/toast';
import Loading from '@/components/loading';
import { CountDown } from '@hb/platformui-h5';
import dayjs from 'dayjs';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import { playContinuousVideo } from '@/pages/welfare/common/continuous-video-engine';
import { addWidget } from '@/pages/welfare/common/bridge';
import { queryUserWidgetStatus } from '@/pages/welfare/common/helper';
import { WIDGET_TEMPLATE_ID, WIDGET_SOURCE } from '@/pages/welfare/consts';
import { env } from '@hb/NBridge/lib/h5';
import { getTasksByTime, isExpired } from '../common/utils';
import { BUTTON_BG } from '../common/constants';

const WIDGET_POPUP_SHOWN_KEY = 'meal_sleep_subsidy_widget_popup_shown';

export default {
  name: 'ActionButton',
  components: { CountDown },
  props: {
    taskGroup: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      showGuidePopup: false,
      showRewardPopup: false,
      showWidgetPopup: false,
      rewardAmount: 0,
    };
  },

  computed: {
    tasksByTime() {
      const list = this.taskGroup.subsidyDetailInfos || [];
      return getTasksByTime(list);
    },
    canClick() {
      const { current } = this.tasksByTime;
      return !!(current && current.taskStatus !== 'FINISHED');
    },
    buttonClass() {
      return this.canClick ? 'is-active' : 'is-disabled';
    },
    buttonBg() {
      const bg = this.canClick ? BUTTON_BG.ACTIVE : BUTTON_BG.DISABLED;
      return { backgroundImage: `url('${bg}')` };
    },
    buttonText() {
      const { current, next } = this.tasksByTime;
      // 可点击：显示"领取{当前任务名}"
      if (this.canClick) {
        return `领取${current.taskName || '补贴'}`;
      }
      // 不可点击：显示"XX点领{下一个任务名}"
      if (next && next.startTime) {
        const [hour, minute] = next.startTime.split(':');
        const time = minute === '00' ? `${hour}点` : `${hour}:${minute}`;
        return `${time}领${next.taskName || '补贴'}`;
      }
      return '暂无任务';
    },
    currentBountyCount() {
      const { current } = this.tasksByTime;
      return (current && current.taskBountyCount) || 0;
    },
    countdownSeconds() {
      const { next } = this.tasksByTime;
      if (!next || !next.startTime) return 0;
      const today = dayjs().format('YYYY-MM-DD');
      let target = dayjs(`${today} ${next.startTime}`);
      // 如果 next 已过期（循环到明天的任务）或 startTime 已过，加一天
      if (isExpired(next) || target.isBefore(dayjs())) {
        target = target.add(1, 'day');
      }
      return Math.max(0, target.diff(dayjs(), 'millisecond'));
    },
  },
  watch: {
    'taskGroup.isPopWindow': {
      immediate: true,
      handler(val) {
        this.showGuidePopup = !!val;
      },
    },
    'taskGroup.subsidyDetailInfos': {
      immediate: true,
      handler(list) {
        if (!list || !list.length) return;
        this.checkWidgetPopup();
      },
    },
  },
  methods: {
    reportButtonExpose() {
      const { current, next } = this.tasksByTime;
      const targetTask = current || next || {};
      UBT.moduleExpose({
        pageId: 'platform_sleepinglandingadver',
        categoryId: 'platform',
        moduleId: 'platform_sleepingBtn',
        contentId: 'platform_sleepingBtn',
        exposureTimes: 1,
        businessInfo: {
          btnstates: this.canClick ? '可点击' : '不可点击',
          clk_name: this.buttonText,
          taskname: targetTask?.taskName || '',
          taskid: targetTask?.taskCode || '',
        },
      });
    },

    handleClick() {
      const { current, next } = this.tasksByTime;
      const targetTask = current || next || {};
      UBT.clickButton({
        pageId: 'platform_sleepinglandingadver',
        buttonName: 'platform_sleepingBtn',
        categoryId: 'platform',
        businessInfo: {
          taskid: targetTask?.taskCode || '',
          taskname: targetTask?.taskName || '',
          btnstates: this.canClick ? '可点击' : '不可点击',
          clk_name: this.buttonText,
        },
      });
      if (!this.canClick) return;
      this.triggerVideo();
    },

    handleGuideAction() {
      this.showGuidePopup = false;
      // 看视频任务
      this.triggerVideo();
    },

    handleCountdownFinish() {
      this.$emit('task-refresh');
    },

    async triggerVideo() {
      const {
        advertisementUnitId, taskCode, stageOffers = [], currentProgress = 0,
      } = this.taskGroup.taskRelationCouponInfo || {};

      // currentProgress 就是当前要看的视频下标（前面的都已完成）
      if (currentProgress >= stageOffers.length) return;

      try {
        const result = await playContinuousVideo({
          firstVideoParams: {
            adUnitId: advertisementUnitId,
            taskCode,
            rewardCount: stageOffers[currentProgress].totalAmount,
          },
          getNextVideoParams: ({ currentIndex }) => {
            const nextIdx = currentProgress + currentIndex + 1;
            if (nextIdx >= stageOffers.length) return null;
            return {
              adUnitId: advertisementUnitId,
              taskCode,
              rewardCount: stageOffers[nextIdx].totalAmount,
            };
          },
          bannerAdsInfo: {},
          toastConfig: {
            none_completed_failed: '拉取视频失败，请稍后再试',
            none_completed_closed: '观看中断未获奖励，请重新点击观看',
            mid_video_failed: '拉取视频失败，请稍后再试',
            mid_video_closed: '观看中断未获奖励，请重新点击观看',
          },
        });

        if (!result.success) return;

        // 从 currentProgress 开始，取 completedCount 个 stage 的 totalAmount 求和
        const reward = stageOffers
          .slice(currentProgress, currentProgress + result.completedCount)
          .reduce((sum, s) => sum + (Number(s.totalAmount) || 0), 0);

        if (reward > 0) {
          this.rewardAmount = reward;
          this.showRewardPopup = true;
        }

        this.$emit('task-refresh');
      } catch (error) {
        Loading.close();
        Toast.show('拉取视频失败，请稍后再试');
      }
    },

    closeRewardPopup() {
      this.showRewardPopup = false;
    },

    async checkWidgetPopup() {
      if (!env.isEasyBikeApp) return;
      if (this.canClick) return;
      const lastShown = localStorage.getItem(WIDGET_POPUP_SHOWN_KEY);
      if (lastShown) {
        const twoDays = 2 * 24 * 60 * 60 * 1000;
        if (Date.now() - Number(lastShown) < twoDays) return;
      }
      const { hasAddedComponent } = await queryUserWidgetStatus();
      if (hasAddedComponent) return;
      this.showWidgetPopup = true;
      localStorage.setItem(WIDGET_POPUP_SHOWN_KEY, String(Date.now()));
    },

    handleWidgetAction() {
      this.showWidgetPopup = false;
      addWidget({ templateId: WIDGET_TEMPLATE_ID, source: WIDGET_SOURCE });
    },
  },
};
</script>

<style lang="less" scoped>
.action-button-wrap {
  position: absolute;
  bottom: 70px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;

  .action-button {
    width: 271px;
    height: 71px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    position: relative;

    .btn-badge {
      position: absolute;
      top: -12px;
      right: -8px;
      padding: 6px 8px;
      background: linear-gradient(90deg, #ff589f 0%, #ff3028 52%, #ff8628 100%);
      border: 1px solid #fff;
      border-radius: 88px 88px 88px 0;
      font-size: 13px;
      font-weight: 600;
      color: #fff;
      white-space: nowrap;
      line-height: 1;
    }

    .btn-play-icon {
      width: 26px;
      height: 26px;
    }

    .btn-text {
      max-width: 220px;
      font-size: 25px;
      font-weight: 600;
      color: #fff;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .countdown-wrap {
    position: absolute;
    top: 0.8rem;
    font-size: 18px;
    text-align: center;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 255px;
    font-weight: 600;

    span {
      flex-shrink: 0;
    }

    span:last-child {
      flex-shrink: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .count-down-num {
      font-weight: 600;
      color: #fff;
      font-family: 'DIN Alternate', sans-serif;
      font-size: 18px;
      flex-shrink: 0;
    }
  }

  .popup {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    z-index: 999;

    .popup-box {
      width: 265px;
      height: 345px;
      padding-top: 154px;
      background-image: url('https://m.hellobike.com/c676444b_Group%202130969830-2.png');
      background-repeat: no-repeat;
      background-position: top center;
      background-size: 100% auto;
      box-sizing: border-box;
      text-align: center;
      position: relative;
    }

    .popup-title {
      font-size: 20px;
      font-weight: 600;
      line-height: 1;
      background: linear-gradient(118deg, #000 0%, #ab6f00 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0 auto 24px;
      max-width: 230px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .popup-amount-wrap {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 4px;
      margin-bottom: 32px;
      color: #ff5c0a;
    }

    .popup-amount {
      font-family: 'DIN Alternate', sans-serif;
      font-size: 45px;
      font-weight: 700;
      line-height: 1;
    }

    .popup-amount-text {
      font-size: 18px;
      font-weight: 500;
      line-height: 1;
    }

    .popup-btn {
      width: 193px;
      height: 44px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(90deg, #ff5523 0%, #ff3e36 100%);
      color: #fff;
      font-size: 18px;
      font-weight: 600;
      border-radius: 12px;
    }

    .popup-close {
      margin-top: 30px;
      width: 32px;
      height: 32px;
    }

    &.add-components .popup-box {
      background-image: url('https://m.hellobike.com/38c9ac84_Group%202130969832%20(1).png');
    }
  }
}
</style>
